import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FilloutTestimonialsSection = () => {
  const { t } = useTranslation('fillout');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: "Jennifer Adams",
      role: "Product Manager",
      company: "E-commerce Solutions",
      avatar: "JA",
      rating: 5,
      text: "Daniel created a complex order form with payment processing that increased our conversion rate by 35%. The Fillout integration with our inventory system works flawlessly.",
      project: "E-commerce Order System",
      results: ["35% higher conversions", "Real-time inventory sync", "Automated order processing"]
    },
    {
      name: "Robert Kim",
      role: "Operations Director", 
      company: "TechStart Corp",
      avatar: "RK",
      rating: 5,
      text: "Our employee onboarding was completely transformed. The team collaboration features and approval workflows in Fillout saved us 15 hours per week.",
      project: "Team Onboarding Portal",
      results: ["15 hours saved weekly", "100% digital workflow", "Improved compliance"]
    },
    {
      name: "Lisa Martinez",
      role: "Agency Owner",
      company: "Digital Pro Agency",
      avatar: "LM", 
      rating: 5,
      text: "The white-label Fillout solution Daniel built allows us to offer branded forms to all our clients. It's become a key differentiator for our agency.",
      project: "White-label Client Portal",
      results: ["Professional branding", "Client satisfaction up 80%", "Recurring revenue stream"]
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const current = testimonials[currentTestimonial];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-muted/30 via-background to-primary/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3 sm:space-y-4 mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
            {t('testimonials.title')}
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Main Testimonial Card */}
          <Card className="relative overflow-hidden border-primary/20 shadow-2xl hover:shadow-3xl transition-all duration-500 bg-gradient-to-br from-background/95 to-background/90 backdrop-blur-lg">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary rounded-full transform translate-x-32 -translate-y-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent rounded-full transform -translate-x-24 translate-y-24"></div>
            </div>

            <CardContent className="relative z-10 p-4 sm:p-6 md:p-8 lg:p-12">
              <div className="grid md:grid-cols-3 gap-6 sm:gap-8 items-center">
                {/* Client Info */}
                <div className="space-y-4 sm:space-y-6 text-center md:text-left">
                  <div className="flex items-center gap-3 sm:gap-4 justify-center md:justify-start">
                    <Avatar className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-primary/20">
                      <AvatarImage src="" alt={current.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-bold text-base sm:text-lg">
                        {current.avatar}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-center md:text-left">
                      <h3 className="font-bold text-base sm:text-lg">{current.name}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground">{current.role}</p>
                      <p className="text-xs sm:text-sm font-medium text-primary">{current.company}</p>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-2 justify-center md:justify-start">
                    {[...Array(current.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                    <span className="text-xs sm:text-sm text-muted-foreground ml-2">5.0/5.0</span>
                  </div>

                  {/* Project Badge */}
                  <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 px-2 sm:px-3 py-1 text-xs sm:text-sm mx-auto md:mx-0 w-fit">
                    {current.project}
                  </Badge>
                </div>

                {/* Testimonial Content */}
                <div className="md:col-span-2 space-y-4 sm:space-y-6">
                  <div className="relative">
                    <Quote className="absolute -top-1 sm:-top-2 -left-1 sm:-left-2 w-6 h-6 sm:w-8 sm:h-8 text-primary/20" />
                    <blockquote className="text-base sm:text-lg md:text-xl leading-relaxed italic pl-4 sm:pl-6">
                      "{current.text}"
                    </blockquote>
                  </div>

                  {/* Results */}
                  <div className="space-y-2 sm:space-y-3">
                    <h4 className="font-semibold text-foreground text-sm sm:text-base">Key Results:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                      {current.results.map((result, index) => (
                        <div key={index} className="flex items-center gap-2 bg-primary/5 rounded-lg p-2 sm:p-3">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full flex-shrink-0"></div>
                          <span className="text-xs sm:text-sm font-medium">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 sm:mt-8 px-4 sm:px-0">
            <Button
              variant="outline"
              size="lg"
              onClick={prevTestimonial}
              className="rounded-full border-primary/20 hover:border-primary/40 hover:bg-primary/5"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            {/* Dots Indicator */}
            <div className="flex gap-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial 
                      ? 'bg-primary scale-125' 
                      : 'bg-primary/30 hover:bg-primary/50'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="lg"
              onClick={nextTestimonial}
              className="rounded-full border-primary/20 hover:border-primary/40 hover:bg-primary/5"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Social Proof */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-8 px-8 py-4 bg-background/80 backdrop-blur-sm rounded-full border border-primary/10">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {['JA', 'RK', 'LM', 'TS', 'MH'].map((initials, i) => (
                  <Avatar key={i} className="w-8 h-8 border-2 border-background">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <span className="text-sm text-muted-foreground">40+ Happy Clients</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium">4.9/5 Average Rating</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FilloutTestimonialsSection;